import React, { ReactNode, InputHTMLAttributes, FormEvent } from 'react';
import Media from 'react-media';

import { FieldContainer, FieldEmotion, Label, InputEmotion, Caption, Icon } from './styles';
import { ViewHint } from '../Hint';
import { media } from '../../styles/MediaQueries';
import { SerializedStyles } from '@emotion/react';
import MaskedInput from 'react-text-mask';
import { InputHint, Position } from './Hint';

export type HintPosition = Position;

export const viewType = ['small', 'medium', 'big'] as const;
export type ViewType = typeof viewType[number];

export interface Props {
  label?: string;
  id: string;
  value?: any;
  defaultValue?: any;
  name?: string;
  view?: ViewType;
  isLabelShort?: boolean;
  caption?: ReactNode;
  isDisabled?: boolean;
  icon?: ReactNode;
  styles?: {
    container?: SerializedStyles;
    field?: SerializedStyles;
    label?: SerializedStyles;
    input?: SerializedStyles;
  };
  hint?: ReactNode;
  hintInfo?: ReactNode;
  hintError?: ReactNode;
  hintWarning?: ReactNode;
  hintView?: ViewHint;
  hintPosition?: HintPosition;
  allowedSymbols?: RegExp;
  mask?: (string | RegExp)[];
  onChange?: (e: FormEvent<HTMLInputElement>) => void;
  isLabelAboveValueOnMobile?: boolean;
}

export type InputProps = Props & InputHTMLAttributes<HTMLInputElement>;

export function Input(props: InputProps) {
  const {
    label,
    id,
    view = 'medium',
    isLabelShort,
    isDisabled,
    caption,
    icon,
    hint,
    hintInfo,
    hintError,
    hintWarning,
    hintView = 'error',
    hintPosition = 'right',
    styles,
    allowedSymbols,
    mask,
    isLabelAboveValueOnMobile,
    ...other
  } = props;

  const isHintVisible = Boolean(hint || hintInfo || hintError || hintWarning);

  return (
    <Media queries={media}>
      {media => {
        const hintPositionAllowable = media.mobile ? 'bottom-static' : hintPosition;

        return (
          <FieldContainer
            isHintDefault={hintPosition === 'default' || hintPositionAllowable === 'bottom-static'}
            isHintVisible={isHintVisible}
            view={view}
            css={styles?.container}
          >
            <FieldEmotion
              isDisabled={isDisabled}
              isLabelAboveValueOnMobile={isLabelAboveValueOnMobile}
              sizeInput={view}
              css={styles?.field}
            >
              {label && (
                <Label htmlFor={id} size={view} isShort={isLabelShort} css={styles?.label}>
                  {label}
                </Label>
              )}

              <MaskedInput
                mask={mask ? mask : false}
                guide={false}
                {...props}
                render={(ref, props) => (
                  <InputEmotion
                    ref={ref as (instance: HTMLInputElement | null) => void}
                    id={id}
                    sizeInput={view}
                    disabled={isDisabled}
                    {...other}
                    css={styles?.input}
                    onKeyDown={event => {
                      if (props.onKeyDown) {
                        props.onKeyDown(event);
                      }

                      if (allowedSymbols && !event.key.match(allowedSymbols)) {
                        event.preventDefault();
                      }
                    }}
                    onChange={event => {
                      if (
                        allowedSymbols &&
                        !event.target.value.match(allowedSymbols) &&
                        event.target.value !== ''
                      ) {
                        event.preventDefault();
                      } else {
                        props.onChange && props.onChange(event);
                      }
                    }}
                  />
                )}
              />

              {caption && <Caption>{caption}</Caption>}
              {icon && <Icon>{icon}</Icon>}
            </FieldEmotion>

            <InputHint
              name={props.name}
              view={hintView}
              position={hintPositionAllowable}
              content={hint}
              info={hintInfo}
              warning={hintWarning}
              error={hintError}
            />
          </FieldContainer>
        );
      }}
    </Media>
  );
}
